// src/components/EditDialog.js
import React, { useState, useEffect } from "react";

/**
 * Minimal “Edit” modal used by the admin dashboard.
 *
 * Props
 * -----
 * open   : boolean                     – whether the dialog is visible
 * onClose: () => void                  – called when user clicks “Cancel”
 * record : object                      – full record from the table row
 * onSave : (patch: object) => Promise  – called with PATCH payload
 * type   : "existing" | "new"          – which table the row came from
 */
export default function EditDialog({ open, onClose, record, onSave, type }) {
  // 1️⃣  Hooks always run unconditionally and in the same order
  const [form, setForm] = useState(() => ({ ...record }));

  // keep local copy in sync if the parent selects a different row
  useEffect(() => setForm({ ...record }), [record]);

  // 2️⃣  Bail‑out after hooks
  if (!open) return null;

  // ---------------- helpers -----------------
  const handleChange = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleCheckbox = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.checked }));

  const text = (field, label, extraProps = {}) => (
    <div style={{ marginBottom: 8 }}>
      <label style={{ fontWeight: 600 }}>{label}</label>
      <input
        {...extraProps}
        style={{ width: "100%" }}
        value={form[field] ?? ""}
        onChange={handleChange(field)}
      />
    </div>
  );

  const bool = (field, label) => (
    <div style={{ marginBottom: 8 }}>
      <label>
        <input
          type="checkbox"
          checked={!!form[field]}
          onChange={handleCheckbox(field)}
          style={{ marginRight: 6 }}
        />
        {label}
      </label>
    </div>
  );

  // --------------- render -------------------
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,.4)",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        zIndex: 9999,
      }}
    >
      <div
        style={{
          background: "#fff",
          padding: 24,
          width: 500,
          maxHeight: "90vh",
          overflowY: "auto",
          borderRadius: 8,
          boxShadow: "0 4px 16px rgba(0,0,0,.25)",
        }}
      >
        <h3 style={{ marginTop: 0 }}>
          Edit {type === "existing" ? "Onboarding (Existing)" : "Onboarding (New)"} – #
          {record.user_id}
        </h3>

        {/* 👉  QUICK & DIRTY: expose every common column */}
        {text("first_name", "First Name")}
        {text("last_name", "Last Name")}
        {text("email1", "Primary Email")}
        {text("email2", "Secondary Email")}
        {text("phone_number", "Phone")}
        {text("date_of_birth", "Date of Birth", { type: "date" })}
        {text("address_line_1", "Address Line 1")}
        {text("address_line_2", "Address Line 2")}
        {text("address_city", "City")}
        {text("postcode", "Postcode")}
        {text("county", "County")}
        {text("occupation", "Occupation")}
        {text("working_hours", "Working Hours")}
        {text("payment_full_name", "Payment Full Name")}
        {text("payment_sort_code", "Sort Code")}
        {text("payment_account_number", "Account Number")}
        {text("referral1", "Referral 1")}
        {text("referral2", "Referral 2")}
        {text("user_code", "User Code")}
        {type === "new" && text("crm_password", "CRM Password")}
        {text("status_notes", "Status Notes")}

        {/* booleans */}
        {bool("high_demand_events", "High Demand Events")}
        {bool("buying_more_tickets", "Buying More Tickets")}
        {bool("transfer_service", "Express Transfer Service")}
        {bool("allow_tt_signups", "Allow TT Sign‑ups")}
        {bool("all_tt_manage_passwords", "All‑TT Manage Passwords")}
        {bool("allow_tt_create_emails", "Allow TT Create Emails")}
        {bool("live_rebate_updates", "Live Rebate Updates")}
        {bool("service_agreement", "Service Agreement")}
        {bool("privacy_policy", "Privacy Policy")}

        <div style={{ marginTop: 16, textAlign: "right" }}>
          <button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </button>
          <button
            onClick={() => onSave(form)}
            style={{
              background: "#008cfa",
              color: "#fff",
              padding: "6px 16px",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
            }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
